import React, { useMemo, useState, useEffect } from 'react';
import { GameState, DisplayFixture } from '../../domain/entities';
import { View } from '../../App';
import { OverviewTab } from './dashboard/OverviewTab';
import { ActionAlertsPanel } from './dashboard/ActionAlertsPanel';
import { QuickNavigationBar } from './dashboard/QuickNavigationBar';
import { TeamFixtures } from '../shared/TeamFixtures';
import { TeamPlayedPanel } from './dashboard/TeamPlayedPanel';
import './dashboard/Dashboard.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlayCircle, faChartLine } from '@fortawesome/free-solid-svg-icons';
import { DashboardHelpModal } from '../shared/DashboardHelpModal';
import { FixturesHelper } from '../../services/FixturesHelper';

// Props for the main screen component
export interface DashboardScreenProps {
    gameState: GameState;
    onPlayWeek: () => void;
    onGoToEndOfSeason?: () => void;
    onMarkNewsAsRead: (id?: string) => void;
    isSimulating: boolean;
    pushView: (view: View) => void;
    isAiPunditEnabled: boolean;
    showRatingsOnMatchReport: boolean;
    onPlayerConversation: (playerId: number, choice: 'praise' | 'promise' | 'show_faith' | 'demand_improvement' | 'warn_dropped' | 'dismiss_concerns' | 'assert_authority') => Promise<{ success: boolean; message: string; }>;
    onOfferFinancialIncentive: (playerId: number, type: 'wage' | 'bonus', amount: number) => Promise<{ success: boolean; message: string; }>;
    onCacheAiMatchReport: (fixture: DisplayFixture, report: { title: string; message: string; }) => void;
    initialTab?: 'overview' | 'my_fixtures' | 'player_statistics';
}

// Props for the sub-tab components
export type TabProps = Omit<DashboardScreenProps, 'onPlayWeek' | 'initialTab'>;

export const DashboardScreen = ({ gameState, onPlayWeek, onGoToEndOfSeason, isSimulating, initialTab, pushView, ...rest }: DashboardScreenProps) => {
    const [activeTab, setActiveTab] = useState<'overview' | 'my_fixtures' | 'player_statistics'>(initialTab || 'overview');
    const [isHelpOpen, setIsHelpOpen] = useState(false);
    
    const nextEvent = useMemo(() => {
        const currentWeek = gameState.currentWeek;

        // Get all fixtures using the new structure
        const allFixtures = FixturesHelper.getAllFixtures(gameState.fixtures);
        const upcomingFixtures = allFixtures.filter(f => f.week >= currentWeek && !f.result);

        if (upcomingFixtures.length === 0) {
            return { name: 'End of Season', isFinal: false, cupName: null, isViewFinal: false };
        }

        // Find the next week with fixtures
        const nextEventWeek = Math.min(...upcomingFixtures.map(f => f.week));

        // Check if player has a league game that week
        const playerFixturesThisWeek = upcomingFixtures.filter(f => f.week === nextEventWeek && (f.home === gameState.playerTeamName || f.away === gameState.playerTeamName));
        const playerLeagueGames = playerFixturesThisWeek.filter(f => 'leagueName' in f && f.leagueName);
        const playerCupGames = playerFixturesThisWeek.filter(f => 'cupName' in f && f.cupName);

        // Check cup events for this week
        const cupsWithFixturesThisWeek: string[] = [];
        Object.entries(gameState.fixtures.cups || {}).forEach(([cupName, weeks]) => {
            const weekFixtures = Object.values(weeks).flat().filter(f => Math.floor(f.week) === Math.floor(nextEventWeek) && !f.result);
            if (weekFixtures.length > 0) {
                cupsWithFixturesThisWeek.push(cupName);
            }
        });

        // Check if the current round is THE FINAL (last round) AND it's happening this week
        let isCupFinal = false;
        let cupWithFinal = null;
        Object.entries(gameState.cups).forEach(([cupName, cup]) => {
            if (cup && cup.rounds && cup.rounds.length > 0) {
                const currentRound = cup.rounds[cup.currentRound];
                const isLastRound = cup.currentRound === cup.rounds.length - 1;
                if (currentRound && Math.floor(currentRound.week) === Math.floor(nextEventWeek) && isLastRound) {
                    isCupFinal = true;
                    cupWithFinal = { ...cup, name: cupName };
                }
            }
        });

        // Check if player is in the final (has a fixture in the final round that week)
        const playerInFinal = cupWithFinal && playerCupGames.some(f => 'cupName' in f && f.cupName === cupWithFinal.name);

        // If player has a cup game, show it as "Play [Round Name]"
        if (playerCupGames.length > 0) {
            const cupGameFixture = playerCupGames[0];
            const cupName = 'cupName' in cupGameFixture ? cupGameFixture.cupName : null;
            if (cupName && gameState.cups[cupName]) {
                const cup = gameState.cups[cupName];
                const roundName = cup.rounds[cup.currentRound]?.name || 'Cup Round';
                return { name: roundName, isFinal: isCupFinal, cupName, isViewFinal: false };
            }
            return { name: 'Cup Round', isFinal: false, cupName, isViewFinal: false };
        }

        // If player has a league game, show "Play Week [N]"
        if (playerLeagueGames.length > 0) {
            return { name: `Week ${Math.floor(nextEventWeek)}`, isFinal: false, cupName: null, isViewFinal: false };
        }

        // No player game this week - check for cup final (show finals as an event regardless of participation)
        if (isCupFinal && cupWithFinal) {
            return { name: `${cupWithFinal.name} Final`, isFinal: true, cupName: cupWithFinal.name || null, isViewFinal: true };
        }

        // Just a week with no player activity
        return { name: `Week ${Math.floor(nextEventWeek)}`, isFinal: false, cupName: null, isViewFinal: false };
    }, [gameState]);

     useEffect(() => {
        if (initialTab) {
            setActiveTab(initialTab);
        }
    }, [initialTab]);

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h1><FontAwesomeIcon icon={faChartLine} style={{ marginRight: '8px', color: 'var(--positive-color)' }} />Home</h1>
                <button
                    className="button"
                    onClick={() => setIsHelpOpen(true)}
                    style={{ padding: '8px 12px', fontSize: '0.9em' }}
                    title="Help"
                >
                    Help
                </button>
            </div>
            <p style={{ marginBottom: '24px', opacity: 0.9 }}>
                Monitor your team's performance, upcoming fixtures, and key squad information.
            </p>

            <ActionAlertsPanel gameState={gameState} pushView={pushView} />

            <QuickNavigationBar pushView={pushView} />

            <div style={{ margin: '0 0 16px 0' }}>
                 <button className="dashboard-action-button" onClick={nextEvent.name === 'End of Season' ? onGoToEndOfSeason : onPlayWeek} disabled={isSimulating || (nextEvent.name === 'End of Season' && !onGoToEndOfSeason)} data-ai-target="play-week">
                    {isSimulating ? 'Simulating...' : nextEvent.name === 'End of Season' ? <>Go to End of Season</> : nextEvent.isViewFinal ? <>View {nextEvent.name}</> : <><FontAwesomeIcon icon={faPlayCircle} style={{ marginRight: '8px' }} />Play {nextEvent.name}</>}
                </button>
            </div>

            <div className="tabs">
                <button className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>Overview</button>
                <button className={`tab-button ${activeTab === 'my_fixtures' ? 'active' : ''}`} onClick={() => setActiveTab('my_fixtures')}>My Fixtures</button>
                <button className={`tab-button ${activeTab === 'player_statistics' ? 'active' : ''}`} onClick={() => setActiveTab('player_statistics')}>Player Statistics</button>
            </div>

            {activeTab === 'overview' && <OverviewTab gameState={gameState} isSimulating={isSimulating} pushView={pushView} {...rest} />}
            {activeTab === 'my_fixtures' && <TeamFixtures teamName={gameState.playerTeamName} leagueName={gameState.playerLeagueName} gameState={gameState} isPlayerTeam={true} pushView={pushView} {...rest} />}
            {activeTab === 'player_statistics' && <TeamPlayedPanel gameState={gameState} pushView={pushView} isAiPunditEnabled={rest.isAiPunditEnabled} showRatingsOnMatchReport={rest.showRatingsOnMatchReport} onCacheAiMatchReport={rest.onCacheAiMatchReport} />}

            <DashboardHelpModal isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} />
        </>
    );
};